import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { createClient } from '@/lib/supabase';
import { 
  initializeGame, 
  processAction, 
  advancePhase, 
  determineWinner, 
  makeBotDecision, 
  isRoundComplete, 
  getActivePlayers,
  getNextActivePlayer,
  canPlayerCheck,
  evaluateHand,
  calculateWinProbability,
  calculateOuts
} from '@/poker/gameLogic';
import { Card, CardStack } from '@/components/Card';
import { PlayersRow } from '@/components/PlayerAvatar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { X, Trophy, Coins, LogOut, RotateCcw, Settings, Info } from 'lucide-react';
import type { GameState, Player } from '@/poker/types';
import toast from 'react-hot-toast';

// Helper function to extract display username (sense els últims 4 caràcters)
const getDisplayUsername = (fullUsername: string): string => {
  return fullUsername.length > 4 ? fullUsername.slice(0, -4) : fullUsername;
};

// Temps entre accions dels bots (ms)
const BOT_THINK_TIME_MIN = 1200;
const BOT_THINK_TIME_MAX = 2500;
const SHOWDOWN_DELAY = 2000;

export default function OnlineGamePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateChips } = useAuth();
  const gameId = location.state?.gameId;
  
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showRaiseMenu, setShowRaiseMenu] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [maxRounds, setMaxRounds] = useState(5);
  const [currentDealerIndex, setCurrentDealerIndex] = useState(0);
  const [playerChips, setPlayerChips] = useState<Record<string, number>>({});
  const [showHandsGuide, setShowHandsGuide] = useState(false);
  const [isClosingHandsGuide, setIsClosingHandsGuide] = useState(false);
  const [showOutsInfo, setShowOutsInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isClosingSettings, setIsClosingSettings] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isClosingInfo, setIsClosingInfo] = useState(false);
  const [showProbabilities, setShowProbabilities] = useState(true);
  const [showFinalSummary, setShowFinalSummary] = useState(false);
  const [initialChips, setInitialChips] = useState(200);
  const [playersReady, setPlayersReady] = useState<string[]>([]);
  const [myPlayerIndex, setMyPlayerIndex] = useState<number>(-1);
  
  const supabase = createClient();
  const processingRef = useRef(false);
  const channelRef = useRef<any>(null);
  const initializedRef = useRef(false);
  const botTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentRoundRef = useRef(currentRound);

  // Mantenir currentRoundRef actualitzat
  useEffect(() => {
    currentRoundRef.current = currentRound;
  }, [currentRound]);

  // Guardar estat del joc a Supabase
  const saveGameState = useCallback(async (state: GameState, round?: number): Promise<boolean> => {
    if (!gameId) return false;

    // 1. Llegir updated_at actual
    const { data: gameRow, error: readError } = await supabase
      .from('online_games')
      .select('updated_at')
      .eq('id', gameId)
      .single();

    if (readError || !gameRow) {
      console.error('Error reading updated_at:', readError);
      return false;
    }
    const prevUpdatedAt = gameRow.updated_at;

    // 2. Preparar updateData
    const updateData: any = { 
      game_state: state, 
      updated_at: new Date().toISOString() 
    };
    if (round !== undefined) {
      updateData.current_round = round;
    }

    // 3. Intentar guardar només si updated_at no ha canviat
    const { error, count } = await supabase
      .from('online_games')
      .update(updateData)
      .eq('id', gameId)
      .eq('updated_at', prevUpdatedAt)
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Error saving game state:', error);
      return false;
    }
    if (count === 0) {
      // Algú altre ha guardat abans, descartar
      console.warn('Optimistic locking: update skipped, state changed by another player');
      return false;
    }
    // Si count > 0, update correcte
    return true;
  }, [gameId, supabase]);

  // Carregar estat del joc
  const loadGameState = useCallback(async () => {
    if (!gameId || !user || initializedRef.current) return;

    const { data: game, error } = await supabase
      .from('online_games')
      .select('game_state, status, initial_chips, max_rounds, current_round, players_ready')
      .eq('id', gameId)
      .single();

    if (error) {
      console.error('Error loading game:', error);
      toast.error('Error carregant la partida');
      navigate('/online');
      return;
    }

    // Verificar que la partida existeix i està activa
    if (!game) {
      console.error('Game not found');
      toast.error('La partida no existeix');
      navigate('/online');
      return;
    }

    // Verificar que la partida està en progrés
    if (game.status !== 'in_progress' && game.status !== 'waiting') {
      console.error('Game is not active, status:', game.status);
      toast.error('La partida ja ha acabat');
      navigate('/online');
      return;
    }

    setMaxRounds(game.max_rounds || 5);
    setCurrentRound(game.current_round || 1);
    setInitialChips(game.initial_chips || 200);
    setPlayersReady(game.players_ready || []);

    if (game.game_state) {
      const state = game.game_state as GameState;
      if (state && state.players && Array.isArray(state.players) && state.players.length > 0) {
        console.log('Loaded game state with', state.players.length, 'players');
        setGameState(state);
        
        // Si hi ha winner, mostrar el resultat
        if (state.winner) {
          setShowResult(true);
        }
        
        // Guardar fitxes actuals
        const chips: Record<string, number> = {};
        state.players.forEach(p => {
          chips[p.id] = p.chips;
        });
        setPlayerChips(chips);
        
        // Obtenir l'índex del jugador actual
        const { data: gamePlayers } = await supabase
          .from('game_players')
          .select('user_id, player_index')
          .eq('game_id', gameId)
          .order('player_index', { ascending: true });
        
        if (gamePlayers) {
          const myIndex = gamePlayers.findIndex(gp => gp.user_id === user.username);
          if (myIndex === -1) {
            console.error('Current user is not in this game');
            toast.error('No estàs en aquesta partida');
            navigate('/online');
            return;
          }
          setMyPlayerIndex(myIndex);
        }
        
        initializedRef.current = true;
        return;
      }
    }

    // Si no hi ha estat o és invàlid, inicialitzar
    if (game.status === 'in_progress') {
      console.log('No valid game state, initializing...');
      
      const { data: gamePlayers, error: playersError } = await supabase
        .from('game_players')
        .select('user_id, profile_id, player_index')
        .eq('game_id', gameId)
        .order('player_index', { ascending: true });

      if (playersError || !gamePlayers || gamePlayers.length < 2) {
        console.error('Not enough players or error:', playersError);
        toast.error('No hi ha prou jugadors');
        navigate('/online');
        return;
      }

      // Verificar que l'usuari actual està a la partida
      const myIndex = gamePlayers.findIndex(gp => gp.user_id === user.username);
      if (myIndex === -1) {
        console.error('Current user is not in this game');
        toast.error('No estàs en aquesta partida');
        navigate('/online');
        return;
      }
      
      setMyPlayerIndex(myIndex);

      // Carregar perfils
      const profileIds = gamePlayers.map(gp => gp.profile_id).filter(Boolean);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', profileIds);

      const profilesMap = new Map((profiles || []).map(p => [p.id, p]));
      const initialChips = game.initial_chips || 200;

      // Crear jugadors humans
      const humanPlayers: Player[] = gamePlayers.map((gp) => {
        const profile = profilesMap.get(gp.profile_id);
        return {
          id: gp.user_id,
          name: profile?.username ? getDisplayUsername(profile.username) : `Jugador ${gp.player_index + 1}`,
          avatar: '👤',
          chips: initialChips,
          cards: [],
          bet: 0,
          totalBet: 0,
          folded: false,
          hasActed: false,
          isAllIn: false,
          isDealer: false,
          isCurrentPlayer: false,
          isHuman: true,
        };
      });

      // Crear 2 bots
      const botPlayers: Player[] = Array.from({ length: 2 }, (_, i) => ({
        id: `bot-${i + 1}`,
        name: `Bot ${i + 1}`,
        avatar: '🤖',
        chips: initialChips,
        cards: [],
        bet: 0,
        totalBet: 0,
        folded: false,
        hasActed: false,
        isAllIn: false,
        isDealer: false,
        isCurrentPlayer: false,
        isHuman: false,
      }));

      // Intercalar jugadors humans i bots per millor experiència
      const allPlayers = humanPlayers.length >= 2 
        ? [humanPlayers[0], botPlayers[0], humanPlayers[1], botPlayers[1]]
        : [...humanPlayers, ...botPlayers];

      const newGameState = initializeGame(allPlayers, 0);
      
      // Guardar fitxes inicials
      const chips: Record<string, number> = {};
      newGameState.players.forEach(p => {
        chips[p.id] = p.chips;
      });
      setPlayerChips(chips);
      
      console.log('Created new game state');
      setGameState(newGameState);
      await saveGameState(newGameState);
      initializedRef.current = true;
    }
  }, [gameId, supabase, user, navigate, saveGameState]);

  // Cancel·lar timeout de bot quan canvia la ronda
  useEffect(() => {
    if (botTimeoutRef.current) {
      clearTimeout(botTimeoutRef.current);
      botTimeoutRef.current = null;
    }
  }, [currentRound]);

  useEffect(() => {
    if (!gameId) return;

    const channel = supabase
      .channel(`online-game-${gameId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'online_games',
          filter: `id=eq.${gameId}`,
        },
        (payload) => {
          if (payload.new.game_state) {
            const newState = payload.new.game_state as GameState;
            if (newState?.players?.length > 0) {
              setGameState(prev => {
                if (!prev) return newState;
                if (JSON.stringify(prev) !== JSON.stringify(newState)) {
                  // Reiniciar processingRef quan canvia l'estat per permetre processar bots
                  processingRef.current = false;
                  
                  // Si el nou estat té winner, mostrar el resultat a tots els clients
                  if (newState.winner && !prev.winner) {
                    setShowResult(true);
                  }
                  
                  return newState;
                }
                return prev;
              });
            }
          }
          if (payload.new.current_round !== undefined) {
            const newRound = payload.new.current_round;
            setCurrentRound(newRound);
            
            // Si ha canviat la ronda, reiniciar els estats per permetre jugar
            if (newRound > currentRoundRef.current) {
              console.log('Nova ronda detectada:', newRound, '- reiniciant estats');
              setShowResult(false);
              setMessage(null);
              setIsProcessing(false);
              processingRef.current = false;
              setPlayersReady([]);
            }
          }
          if (payload.new.players_ready !== undefined) {
            console.log('Players ready actualitzat via subscripció:', payload.new.players_ready);
            setPlayersReady(payload.new.players_ready || []);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'online_games',
          filter: `id=eq.${gameId}`,
        },
        (payload) => {
          toast.error('La partida ha estat eliminada');
          navigate('/online');
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'game_players',
          filter: `game_id=eq.${gameId}`,
        },
        (payload) => {
          // Si l'altre jugador ha marxat, expulsar aquest també
          if (payload.old.user_id !== user?.username) {
            toast.error("L'altre jugador ha abandonat la partida");
            setTimeout(() => {
              navigate('/online');
            }, 1500);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [gameId, supabase, user, navigate]);

  // Carregar joc inicial
  useEffect(() => {
    if (!gameId) {
      toast.error("No s'ha trobat la partida");
      navigate('/online');
      return;
    }
    loadGameState();
  }, [gameId, loadGameState, navigate]);

  // Tancar menú de raise quan es clica fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRaiseMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.raise-menu-container')) {
          setShowRaiseMenu(false);
        }
      }
    };
    
    if (showRaiseMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showRaiseMenu]);

  // Mostrar missatge temporal
  const showTemporaryMessage = useCallback((msg: string, duration = 1500) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), duration);
  }, []);

  // Processar torn de bot - TOTS els jugadors poden intentar-ho (optimistic locking)
  const processBotTurn = useCallback(async () => {
    if (!gameState || showResult || processingRef.current) return;
    
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayer || currentPlayer.isHuman || currentPlayer.folded || currentPlayer.isAllIn) return;

    console.log('🤖 Processant bot:', currentPlayer.name, '(jugador', myPlayerIndex, ')');
    processingRef.current = true;
    setIsProcessing(true);

    // Temps de pensar
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 400));

    if (showResult) {
      processingRef.current = false;
      setIsProcessing(false);
      return;
    }

    const decision = makeBotDecision(currentPlayer, gameState);
    const newState = processAction(gameState, gameState.currentPlayerIndex, decision.action, decision.amount);

    const actionMessages: Record<string, string> = {
      fold: `${currentPlayer.name} es retira`,
      check: `${currentPlayer.name} passa`,
      call: `${currentPlayer.name} iguala`,
      raise: `${currentPlayer.name} puja`,
    };
    showTemporaryMessage(actionMessages[decision.action]);

    setGameState(newState);
    await saveGameState(newState);
    
    processingRef.current = false;
    setIsProcessing(false);
  }, [gameState, showResult, saveGameState, showTemporaryMessage, myPlayerIndex]);

  // Gestió del flux del joc - SIMPLIFICADA
  useEffect(() => {
    if (!gameState || showResult) return;
    
    console.log('📊 Game flow check:', {
      phase: gameState.phase,
      currentPlayer: gameState.players[gameState.currentPlayerIndex]?.name,
      isBot: !gameState.players[gameState.currentPlayerIndex]?.isHuman,
      myPlayerIndex,
      processingRef: processingRef.current
    });
    
    // Cancel·lar timeout anterior
    if (botTimeoutRef.current) {
      clearTimeout(botTimeoutRef.current);
      botTimeoutRef.current = null;
    }
    
    // Si ja estem al showdown, mostrar el resultat
    if (gameState.phase === 'showdown') {
      if (gameState.winner) {
        setShowResult(true);
      } else {
        // Calcular el guanyador si no s'ha fet
        const { winner, hand } = determineWinner(gameState);
        setGameState(prev => {
          if (!prev) return prev;
          const updatedPlayers = prev.players.map(p =>
            p.id === winner.id ? { ...p, chips: p.chips + prev.pot } : p
          );
          const newChips: Record<string, number> = {};
          updatedPlayers.forEach(p => {
            newChips[p.id] = p.chips;
          });
          setPlayerChips(newChips);
          return { ...prev, players: updatedPlayers, winner, winningHand: hand };
        });
        setShowResult(true);
      }
      return;
    }
    
    if (processingRef.current) return;

    const activePlayers = getActivePlayers(gameState);
    const playersCanAct = gameState.players.filter(p => !p.folded && !p.isAllIn);

    // Si només queda un jugador no-folded, ha guanyat automàticament
    if (activePlayers.length === 1) {
      const { winner, hand } = determineWinner(gameState);
      processingRef.current = true;
      setTimeout(async () => {
        setGameState(prev => {
          if (!prev) return prev;
          const updatedPlayers = prev.players.map(p =>
            p.id === winner.id ? { ...p, chips: p.chips + prev.pot } : p
          );

          const newChips: Record<string, number> = {};
          updatedPlayers.forEach(p => {
            newChips[p.id] = p.chips;
          });
          setPlayerChips(newChips);

          // Les fitxes es guardaran a la base de dades quan el jugador surti
          // No actualitzar aquí per evitar duplicació de pèrdues

          const newState = {
            ...prev,
            players: updatedPlayers,
            winner,
            winningHand: hand,
            phase: 'showdown' as const,
          };
          saveGameState(newState);
          return newState;
        });
        setShowResult(true);
        processingRef.current = false;
      }, 800);
      return;
    }
    
    // Si tots els jugadors estan all-in (ningú pot actuar), avançar fases automàticament fins showdown
    if (playersCanAct.length === 0 && activePlayers.length > 1 && gameState.phase !== 'river' && gameState.phase !== 'showdown') {
      processingRef.current = true;
      showTemporaryMessage('All-in! Revelant cartes...');
      
      // Avançar automàticament les fases amb delay
      setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          const newState = advancePhase(prev);
          saveGameState(newState);
          return newState;
        });
        processingRef.current = false;
      }, 1500);
      return;
    }

    // Comprovar si la ronda ha acabat
    if (isRoundComplete(gameState)) {
      console.log('🎯 Round complete detected:', {
        phase: gameState.phase,
        isRiver: gameState.phase === 'river',
        processingRef: processingRef.current,
        playersStatus: gameState.players.map(p => ({
          name: p.name,
          hasActed: p.hasActed,
          folded: p.folded,
          isAllIn: p.isAllIn,
          bet: p.bet,
          currentBet: gameState.currentBet
        }))
      });
      
      if (gameState.phase === 'river') {
        console.log('🏆 River phase - calculating winner...');
        processingRef.current = true;
        setTimeout(async () => {
          console.log('🏆 Executing showdown logic...');
          const { winner, hand } = determineWinner(gameState);
          console.log('🏆 Winner determined:', winner?.name, hand);
          setGameState(prev => {
            if (!prev) return prev;
            const updatedPlayers = prev.players.map(p =>
              p.id === winner.id ? { ...p, chips: p.chips + prev.pot } : p
            );

            const newChips: Record<string, number> = {};
            updatedPlayers.forEach(p => {
              newChips[p.id] = p.chips;
            });
            setPlayerChips(newChips);

            const newState = {
              ...prev,
              players: updatedPlayers,
              winner,
              winningHand: hand,
              phase: 'showdown' as const,
            };
            if (myPlayerIndex === 0) saveGameState(newState); // Optimistic locking evita duplicats
            return newState;
          });
          setShowResult(true);
          processingRef.current = false;
          console.log('🏆 Showdown completed, processingRef set to false');
        }, SHOWDOWN_DELAY);
        return;
      } else {
        // TOTS poden intentar avançar de fase (optimistic locking)
        console.log('📈 Advancing phase from', gameState.phase, '- setting processingRef to true');
        processingRef.current = true;
        
        const newState = advancePhase(gameState);
        const phaseNames: Record<string, string> = {
          flop: '🃏 Flop',
          turn: '🃏 Turn',
          river: '🃏 River',
        };
        if (phaseNames[newState.phase]) {
          showTemporaryMessage(phaseNames[newState.phase]);
        }
        
        // Intentar guardar i actualitzar estat local només si té èxit
        saveGameState(newState).then(saveSuccess => {
          if (saveSuccess) {
            setGameState(newState);
            console.log('📈 Phase advanced to', newState.phase, '- state updated locally');
          } else {
            console.log('📈 Phase advance failed (optimistic locking) - skipping local update');
          }
        });
        
        // Alliberar processingRef immediatament (no esperar el save)
        processingRef.current = false;
        console.log('📈 Phase advance attempt initiated - processingRef set to false');
      }
      return;
    }

    // Si és torn d'un bot, programar el seu processament
    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (!currentPlayer) return;

    if (!currentPlayer.isHuman && !currentPlayer.folded && !currentPlayer.isAllIn) {
      // TOTS els jugadors poden intentar processar bots immediatament (optimistic locking)
      botTimeoutRef.current = setTimeout(() => {
        if (!processingRef.current && !showResult && gameState.phase !== 'showdown') {
          processBotTurn();
        }
      }, 300 + Math.random() * 200); // Petit delay aleatori per evitar conflictes
    }
    
    // Si el jugador actual ja ha fet fold o està all-in, avançar al següent (tots poden intentar-ho)
    if (!currentPlayer.isHuman && (currentPlayer.folded || currentPlayer.isAllIn)) {
      const nextIndex = getNextActivePlayer(gameState.players, gameState.currentPlayerIndex);
      if (nextIndex !== gameState.currentPlayerIndex) {
        const newState = { ...gameState, currentPlayerIndex: nextIndex };
        setGameState(newState);
        saveGameState(newState);
      }
    }
  }, [gameState, showResult, processBotTurn, myPlayerIndex, saveGameState, showTemporaryMessage]);

  // Marcar jugador com a ready
  const markPlayerReady = useCallback(async () => {
    if (!gameState || !user || !gameId) return;

    const { data: game } = await supabase
      .from('online_games')
      .select('players_ready')
      .eq('id', gameId)
      .single();

    const currentReady = game?.players_ready || [];
    if (currentReady.includes(user.username)) return;

    const newReady = [...currentReady, user.username];
    setPlayersReady(newReady);
    
    await supabase
      .from('online_games')
      .update({ players_ready: newReady, updated_at: new Date().toISOString() })
      .eq('id', gameId);
  }, [gameState, user, gameId, supabase]);

  // Iniciar nova ronda - TOTS poden intentar-ho (optimistic locking)
  const startNextRound = useCallback(async () => {
    if (!gameState || !gameId) return;
    
    console.log('🎮 Iniciant nova ronda... (jugador', myPlayerIndex, ')');

    const playersWithChips = gameState.players.filter(p => (playerChips[p.id] || p.chips) > 0);
    if (playersWithChips.length < 2) {
      toast.error("La partida ha acabat - un jugador s'ha quedat sense fitxes");
      navigate('/online');
      return;
    }

    const nextDealer = (currentDealerIndex + 1) % gameState.players.length;
    setCurrentDealerIndex(nextDealer);

    const players = gameState.players.map(p => ({
      ...p,
      chips: playerChips[p.id] || p.chips,
      cards: [],
      bet: 0,
      totalBet: 0,
      folded: false,
      hasActed: false,
      isAllIn: false,
      isDealer: false,
      isCurrentPlayer: false,
    }));

    const newGame = initializeGame(players, nextDealer);
    const nextRound = currentRound + 1;
    
    // Actualitzar amb players_ready buit
    await supabase
      .from('online_games')
      .update({ 
        game_state: newGame, 
        current_round: nextRound,
        players_ready: [],
        updated_at: new Date().toISOString()
      })
      .eq('id', gameId);

    setGameState(newGame);
    setCurrentRound(nextRound);
    setPlayersReady([]);
    setShowResult(false);
    setMessage(null);
    processingRef.current = false;
  }, [gameState, playerChips, currentDealerIndex, currentRound, navigate, myPlayerIndex, gameId, supabase]);

  // Detectar quan tots els jugadors estan ready - TOTS poden detectar-ho
  useEffect(() => {
    const checkReadyPlayers = async () => {
      if (!gameId || !showResult || playersReady.length === 0) return;

      const { count } = await supabase
        .from('game_players')
        .select('*', { count: 'exact', head: true })
        .eq('game_id', gameId);

      const totalPlayers = count || 0;
      console.log('Ready check:', playersReady.length, '/', totalPlayers, '(jugador', myPlayerIndex, ')');

      // Si tots els jugadors estan ready, qualsevol pot iniciar la ronda
      if (playersReady.length >= totalPlayers && totalPlayers > 0) {
        console.log('✅ Tots els jugadors ready, iniciant nova ronda...');
        setTimeout(() => {
          startNextRound();
        }, 500);
      }
    };

    checkReadyPlayers();
  }, [playersReady, showResult, gameId, supabase, startNextRound, myPlayerIndex]);

  // Accions del jugador
  const handleAction = useCallback(async (action: 'fold' | 'check' | 'call' | 'raise', amount?: number) => {
    if (!gameState || isProcessing || showResult || !user) return;

    const humanIndex = gameState.players.findIndex(p => p.isHuman && p.id === user.username);
    if (gameState.currentPlayerIndex !== humanIndex) {
      toast.error('No és el teu torn');
      return;
    }

    setIsProcessing(true);
    const newState = processAction(gameState, humanIndex, action, amount);

    setTimeout(async () => {
      setGameState(newState);
      await saveGameState(newState);
      setTimeout(() => {
        setIsProcessing(false);
      }, 200);
    }, 400);
  }, [gameState, isProcessing, showResult, user, saveGameState]);

  const handleFold = () => handleAction('fold');
  const handleCheck = () => handleAction('check');
  const handleCall = () => handleAction('call');
  const handleRaise = (amount: number) => {
    handleAction('raise', amount);
    setShowRaiseMenu(false);
  };

  const handleExitGame = useCallback(async () => {
    if (user && gameId) {
      try {
        // 1. Obtenir chips_before_buy_in de game_players
        const { data: gamePlayer } = await supabase
          .from('game_players')
          .select('chips_before_buy_in, buy_in_paid')
          .eq('game_id', gameId)
          .eq('user_id', user.username)
          .single();

        const finalChipsInGame = playerChips[user.username] || 0;

        let newTotalChips: number;
        if (gamePlayer?.chips_before_buy_in !== null && gamePlayer?.chips_before_buy_in !== undefined) {
          // 2. Càlcul: chips_before_buy_in - initialChips + finalChipsInGame
          // El buy-in ja s'ha descomptat quan la partida comença, per tant:
          // - chips_before_buy_in = fitxes abans del buy-in
          // - initialChips = buy-in que ja s'ha descomptat
          // - finalChipsInGame = fitxes que tens al final del joc
          // - Total = chips_before_buy_in - initialChips + finalChipsInGame
          //   = (chips_before_buy_in - initialChips) + finalChipsInGame
          //   = profile.chips_actuals + finalChipsInGame
          
          // Per partides gratuïtes (initialChips = 0), no es descompta res
          const buyInDeducted = initialChips > 0 ? initialChips : 0;
          newTotalChips = gamePlayer.chips_before_buy_in - buyInDeducted + finalChipsInGame;
          
          console.log('🏁 ONLINE - SORTIDA:', {
            username: user.username,
            chipsBeforeBuyIn: gamePlayer.chips_before_buy_in,
            buyIn: initialChips,
            buyInDeducted,
            finalChipsInGame,
            profit: finalChipsInGame - buyInDeducted,
            newTotalChips,
            calculation: `${gamePlayer.chips_before_buy_in} - ${buyInDeducted} + ${finalChipsInGame} = ${newTotalChips}`
          });
        } else {
          // 3. Fallback: càlcul si chips_before_buy_in no existeix
          const { data: profile } = await supabase
            .from('profiles')
            .select('chips')
            .eq('username', user.username)
            .single();
          
          // Si el buy-in ja s'ha descomptat, simplement afegir les fitxes finals
          const buyInDeducted = initialChips > 0 ? initialChips : 0;
          newTotalChips = (profile?.chips || 0) + finalChipsInGame;
          
          console.log('🏁 ONLINE - SORTIDA (FALLBACK):', {
            username: user.username,
            profileChipsBefore: profile?.chips || 0,
            buyIn: initialChips,
            buyInDeducted,
            finalChipsInGame,
            profit: finalChipsInGame - buyInDeducted,
            newTotalChips,
            calculation: `${profile?.chips || 0} + ${finalChipsInGame} = ${newTotalChips}`
          });
        }
        
        // 4. Actualitzar a la base de dades i en memòria
        await updateChips(newTotalChips);
      } catch (error) {
        console.error('Error actualitzant fitxes:', error);
      }
    }

    // Alliberar la plaça a la sala
    if (gameId && user) {
      try {
        // Eliminar el jugador de game_players - el trigger eliminarà la partida automàticament
        await supabase
          .from('game_players')
          .delete()
          .eq('game_id', gameId)
          .eq('user_id', user.username);

        console.log('Jugador eliminat de la partida');
      } catch (error) {
        console.error('Error alliberant la sala:', error);
      }
    }

    navigate('/online');
  }, [user, updateChips, navigate, gameId, supabase, playerChips, initialChips]);

  // Tancar drawer amb animació
  const closeHandsGuide = useCallback(() => {
    setIsClosingHandsGuide(true);
    setTimeout(() => {
      setShowHandsGuide(false);
      setIsClosingHandsGuide(false);
    }, 300);
  }, []);

  const closeSettings = useCallback(() => {
    setIsClosingSettings(true);
    setTimeout(() => {
      setShowSettings(false);
      setIsClosingSettings(false);
    }, 300);
  }, []);

  const closeInfo = useCallback(() => {
    setIsClosingInfo(true);
    setTimeout(() => {
      setShowInfo(false);
      setIsClosingInfo(false);
    }, 300);
  }, []);

  // Loading state
  if (!gameState || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="text-4xl animate-bounce">🃏</div>
          <div className="text-zinc-500 text-sm">Carregant partida online...</div>
        </div>
      </div>
    );
  }

  const humanPlayer = gameState.players.find(p => p.isHuman && p.id === user.username);
  if (!humanPlayer) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-950">
        <div className="flex flex-col items-center gap-4">
          <div className="text-zinc-500">No ets un jugador d'aquesta partida</div>
          <Button onClick={() => navigate('/online')} variant="outline">
            Tornar
          </Button>
        </div>
      </div>
    );
  }

  const humanIndex = gameState.players.findIndex(p => p.isHuman && p.id === user.username);
  const isHumanTurn = gameState.currentPlayerIndex === humanIndex && !showResult && !humanPlayer.folded && !humanPlayer.isAllIn && !processingRef.current;
  const toCall = gameState.currentBet - humanPlayer.bet;
  const canCheck = canPlayerCheck(gameState, humanIndex);

  // Calcular estadístiques
  const visibleCommunity = gameState.communityCards.filter(c => !c.hidden);
  const currentHand = evaluateHand(humanPlayer.cards, visibleCommunity);
  const activeOpponents = gameState.players.filter(p => !p.folded && p.id !== user.username).length;
  const winProb = calculateWinProbability(humanPlayer.cards, visibleCommunity, activeOpponents);
  const { outs, draws } = calculateOuts(humanPlayer.cards, visibleCommunity);

  const roundToChipDenomination = (amount: number): number => {
    return Math.max(5, Math.round(amount / 5) * 5);
  };

  const minRaiseAmount = roundToChipDenomination(gameState.currentBet + gameState.minRaise);
  const potRaise = roundToChipDenomination(gameState.currentBet + gameState.pot);
  const halfPotRaise = roundToChipDenomination(gameState.currentBet + Math.round(gameState.pot / 2));

  const phaseNames: Record<string, string> = {
    preflop: 'Pre-flop',
    flop: 'Flop',
    turn: 'Turn',
    river: 'River',
    showdown: 'Final',
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col select-none overflow-x-hidden pb-safe touch-none overscroll-none pb-[180px] sm:pb-[200px]">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 pt-3 sm:pt-4 pb-2 shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-[10px] sm:text-xs font-semibold text-zinc-400 uppercase tracking-wider px-2 py-1 bg-zinc-800 rounded">
            {phaseNames[gameState.phase]}
          </span>
          {isHumanTurn && !isProcessing && (
            <span className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-semibold text-amber-400">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="hidden sm:inline">El teu torn</span>
              <span className="sm:hidden">Tu</span>
            </span>
          )}
          {isProcessing && (
            <span className="text-[10px] sm:text-xs text-zinc-500">
              Pensant...
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-zinc-400 bg-zinc-800/80 px-2 sm:px-3 py-1 rounded text-xs sm:text-sm font-semibold">
            <span>{currentRound}</span>
            <span className="text-zinc-600">/</span>
            <span>{maxRounds}</span>
          </div>
          <button
            onClick={() => setShowInfo(true)}
            className="w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            aria-label="Informació"
          >
            <Info className="w-4 h-4 text-zinc-400" />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="w-8 h-8 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            aria-label="Configuració"
          >
            <Settings className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Missatge temporal */}
      {message && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-40 animate-fade-in">
          <div className="bg-zinc-800 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            {message}
          </div>
        </div>
      )}

      {/* Jugadors rivals */}
      <div className="pt-2 sm:pt-3 pb-0 shrink-0">
        <PlayersRow
          players={gameState.players}
          currentPlayerIndex={gameState.currentPlayerIndex}
          humanIndex={humanIndex}
        />
      </div>

      {/* Taula - Cartes comunitàries */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-0 relative -mt-3 sm:-mt-4">
        <div className="flex flex-col items-center justify-center w-full px-3 sm:px-4">
          <div className="relative w-full flex justify-center mb-2 sm:mb-3">
            <div className="absolute -inset-8 sm:-inset-12 bg-gradient-to-b from-emerald-950/20 to-transparent rounded-full blur-3xl" />
            <div className="relative flex gap-1 sm:gap-1.5 md:gap-2 justify-center items-center">
              {gameState.communityCards.map((card, index) => (
                <div
                  key={index}
                  className={cn(
                    'transition-all duration-700',
                    !card.hidden && 'animate-flip-in'
                  )}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <Card card={card} size="md" />
                </div>
              ))}
            </div>
          </div>

          {/* Pot total al centre */}
          <div className="flex flex-col items-center gap-1 sm:gap-1.5">
            <div className="flex items-center gap-2 bg-zinc-800/50 border border-zinc-700 px-3 py-1 sm:py-1.5 rounded-lg">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wider">Pot</span>
              <span className="font-bold text-base text-amber-400 tabular-nums">{gameState.pot}</span>
            </div>

            {gameState.currentBet > 0 && (
              <div className="text-zinc-400 text-xs sm:text-sm">
                Aposta: <span className="font-bold text-white">{gameState.currentBet}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de resultat */}
      {showResult && gameState.winner && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-3 sm:p-4">
          <div className={cn(
            'rounded-2xl sm:rounded-3xl p-6 sm:p-8 text-center max-w-sm w-full relative overflow-hidden',
            gameState.winner.id === user.username
              ? 'bg-gradient-to-b from-amber-900/50 to-zinc-900'
              : 'bg-zinc-900'
          )}>
            {gameState.winner.id === user.username && (
              <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400 mx-auto mb-2 sm:mb-3" />
            )}
            <span className="text-5xl sm:text-6xl mb-3 sm:mb-4 block">{gameState.winner.avatar}</span>
            <h2 className="text-xl sm:text-2xl font-bold mb-1">
              {gameState.winner.id === user.username ? 'Has guanyat!' : `${gameState.winner.name} guanya`}
            </h2>
            <p className="text-zinc-400 mb-3 sm:mb-4 text-xs sm:text-sm">{gameState.winningHand}</p>

            {/* Cartes comunitàries */}
            <div className="mb-3">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Taula</div>
              <div className="flex justify-center gap-1.5">
                {gameState.communityCards.map((card, index) => {
                  const winningHandEval = evaluateHand(gameState.winner!.cards, gameState.communityCards);
                  const isWinningCard = winningHandEval.cards.some(wc => wc.suit === card.suit && wc.rank === card.rank);
                  return (
                    <div key={index} className={cn(isWinningCard && "ring-2 ring-amber-400 rounded-xl")}>
                      <Card card={{ ...card, hidden: false }} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cartes del guanyador */}
            <div className="mb-4">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Cartes del guanyador</div>
              <div className="flex justify-center gap-2">
                {gameState.winner.cards.map((card, index) => {
                  const winningHandEval = evaluateHand(gameState.winner!.cards, gameState.communityCards);
                  const isWinningCard = winningHandEval.cards.some(wc => wc.suit === card.suit && wc.rank === card.rank);
                  return (
                    <div key={index} className={cn(isWinningCard && "ring-2 ring-amber-400 rounded-xl")}>
                      <Card card={{ ...card, hidden: false }} size="sm" />
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-amber-400 font-bold text-xl sm:text-2xl mb-4 sm:mb-6 flex items-center justify-center gap-2">
              <Coins className="w-4 h-4 sm:w-5 sm:h-5" />
              +{gameState.pot}
            </p>

            {currentRound < maxRounds ? (
              <div className="space-y-2">
                {playersReady.includes(user?.username || '') ? (
                  <div className="text-center">
                    <p className="text-emerald-400 font-semibold text-sm mb-1">✓ Preparat!</p>
                    <p className="text-zinc-400 text-xs">Esperant altres jugadors... ({playersReady.length}/2)</p>
                  </div>
                ) : (
                  <Button
                    onClick={markPlayerReady}
                    className="w-full gap-2 h-11 sm:h-12 text-sm sm:text-base font-semibold bg-white text-zinc-900 hover:bg-zinc-100"
                  >
                    Preparat per la següent ronda ({currentRound + 1}/{maxRounds})
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-amber-400 font-semibold text-sm mb-1">🏆 Partida completada!</p>
                  <p className="text-zinc-400 text-xs">{maxRounds} rondes jugades</p>
                </div>
                <Button
                  onClick={() => setShowFinalSummary(true)}
                  className="w-full gap-2 h-11 sm:h-12 text-sm sm:text-base font-semibold bg-white text-zinc-900 hover:bg-zinc-100"
                >
                  <Trophy className="w-3 h-3 sm:w-4 sm:h-4" />
                  Veure resum final
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zona inferior del jugador */}
      <div className="fixed bottom-0 left-0 right-0 px-2 sm:px-4 pb-2 sm:pb-4 pt-0.5 space-y-1.5 sm:space-y-2 bg-gradient-to-t from-zinc-900 via-zinc-900 to-zinc-900 z-30">
        {/* Indicador si el jugador s'ha retirat */}
        {humanPlayer.folded && (
          <div className="text-center text-zinc-500 text-sm py-2">
            T'has retirat d'aquesta mà
          </div>
        )}

        {/* Botons d'acció */}
        {!humanPlayer.folded && !humanPlayer.isAllIn && (
          <div className="flex gap-1.5 sm:gap-2">
            {/* Fold */}
            <Button
              onClick={handleFold}
              disabled={!isHumanTurn}
              variant="outline"
              className={cn(
                "h-10 sm:h-12 px-2 sm:px-4 font-semibold transition-all border-zinc-700 text-zinc-400 text-xs sm:text-base",
                "hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-400",
                "disabled:opacity-40"
              )}
            >
              <X className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Abandonar</span>
            </Button>

            {/* Check/Call */}
            {canCheck ? (
              <Button
                onClick={handleCheck}
                disabled={!isHumanTurn}
                className={cn(
                  "flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-zinc-800 hover:bg-zinc-700 text-white",
                  isHumanTurn && "ring-2 ring-amber-400/50"
                )}
              >
                Passa
              </Button>
            ) : (
              <Button
                onClick={handleCall}
                disabled={!isHumanTurn}
                className={cn(
                  "flex-1 h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-zinc-800 hover:bg-zinc-700 text-white",
                  isHumanTurn && "ring-2 ring-amber-400/50"
                )}
              >
                Iguala <span className="ml-1 text-amber-400">{Math.min(toCall, humanPlayer.chips)}</span>
              </Button>
            )}

            {/* Raise amb menú */}
            <div className="flex-1 relative raise-menu-container">
              <Button
                onClick={() => handleRaise(Math.min(minRaiseAmount, humanPlayer.bet + humanPlayer.chips))}
                disabled={!isHumanTurn || humanPlayer.chips === 0}
                className={cn(
                  "w-full h-10 sm:h-12 text-xs sm:text-base font-semibold transition-all",
                  "bg-amber-500 hover:bg-amber-400 text-zinc-900",
                  "disabled:opacity-40 disabled:bg-zinc-700 disabled:text-zinc-500"
                )}
              >
                {humanPlayer.chips < minRaiseAmount - humanPlayer.bet ? (
                  <span>All-in {humanPlayer.bet + humanPlayer.chips}</span>
                ) : (
                  <span>{gameState.currentBet === 0 ? 'Aposta' : 'Puja'} {minRaiseAmount}</span>
                )}
              </Button>
              {isHumanTurn && !humanPlayer.folded && humanPlayer.chips > minRaiseAmount && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRaiseMenu(!showRaiseMenu)}
                  className="absolute -top-8 right-0 h-7 px-2 text-[10px] sm:text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  + Opcions
                </Button>
              )}
              {/* Menú desplegable */}
              {showRaiseMenu && isHumanTurn && (
                <div className="absolute bottom-full right-0 mb-2 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl p-1 flex flex-col gap-1 z-50 min-w-32">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(halfPotRaise);
                      setShowRaiseMenu(false);
                    }}
                    disabled={humanPlayer.chips < halfPotRaise - humanPlayer.bet}
                    className="justify-between text-xs font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-40 h-8"
                  >
                    <span>½ Pot</span>
                    <span className="text-amber-400">{halfPotRaise}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(potRaise);
                      setShowRaiseMenu(false);
                    }}
                    disabled={humanPlayer.chips < potRaise - humanPlayer.bet}
                    className="justify-between text-xs font-semibold text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-40 h-8"
                  >
                    <span>Pot</span>
                    <span className="text-amber-400">{potRaise}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleRaise(humanPlayer.bet + humanPlayer.chips);
                      setShowRaiseMenu(false);
                    }}
                    className="justify-between text-xs font-bold text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 h-8"
                  >
                    <span>💰 All-in</span>
                    <span>{humanPlayer.chips + humanPlayer.bet}</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cartes del jugador i Info */}
        <div className="flex gap-2 sm:gap-3 items-stretch">
          {/* Cartes del jugador */}
          <button
            onClick={() => setShowHandsGuide(true)}
            className={cn(
              "bg-zinc-900/80 rounded-xl sm:rounded-2xl p-2 sm:p-3 flex items-center justify-center border border-zinc-800 transition-all cursor-pointer shrink-0",
              "hover:border-amber-500/60 active:scale-95",
              isHumanTurn && "border-amber-500/40 shadow-lg shadow-amber-500/10",
              humanPlayer.folded && "opacity-50"
            )}
          >
            <CardStack cards={humanPlayer.cards} overlap={true} size="lg" highlighted={isHumanTurn} />
          </button>

          {/* Info del jugador: Fitxes i Estadístiques */}
          <div className="bg-zinc-900/80 rounded-xl sm:rounded-2xl p-2 sm:p-3 border border-zinc-800 grid grid-cols-2 gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Fitxes */}
            <div className="flex flex-col justify-center">
              <div className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wider mb-0.5">Fitxes</div>
              <div className="text-lg sm:text-2xl font-black text-white tabular-nums">{humanPlayer.chips}</div>
              {humanPlayer.bet > 0 && (
                <div className="text-zinc-500 text-[9px] sm:text-[10px] mt-0.5">
                  Apostat: {humanPlayer.bet}
                </div>
              )}
            </div>

            {/* Estadístiques - Probabilitats */}
            <div className="flex flex-col justify-center border-l border-zinc-700 pl-2 sm:pl-3">
              {humanPlayer.folded ? (
                <div className="text-zinc-500 text-[10px] sm:text-xs text-center">
                  Esperant...
                </div>
              ) : visibleCommunity.length > 0 ? (
                <>
                  <div className="mb-1">
                    <div className="text-zinc-500 text-[9px] sm:text-[10px] uppercase tracking-wider mb-0.5">Mà</div>
                    <div className="text-zinc-200 text-[11px] sm:text-sm font-semibold leading-tight">{currentHand.rank}</div>
                  </div>
                  {showProbabilities && (
                    <div className="space-y-0.5">
                      <div className="flex items-baseline gap-1">
                        <div className="text-zinc-500 text-[9px] sm:text-[10px]">Probabilitats:</div>
                        <div className={cn(
                          "text-sm sm:text-lg font-bold tabular-nums",
                          winProb > 60 ? "text-emerald-400" : winProb > 40 ? "text-amber-400" : "text-red-400"
                        )}>
                          {winProb}%
                        </div>
                      </div>
                      {visibleCommunity.length < 5 && outs > 0 && (
                        <div
                          onClick={() => setShowOutsInfo(true)}
                          className="flex items-baseline gap-1 cursor-pointer hover:text-cyan-300"
                        >
                          <div className="text-zinc-500 text-[9px] sm:text-[10px]">Outs:</div>
                          <div className="text-sm sm:text-base font-bold text-cyan-400 tabular-nums">{outs}</div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-zinc-500 text-[10px] sm:text-xs text-center">
                  Esperant flop...
                </div>
              )}
            </div>
          </div>
        </div>
        {humanPlayer.isAllIn && (
          <div className="text-amber-400 text-sm font-semibold text-center mt-2">
            ALL-IN! Esperant la ronda...
          </div>
        )}
      </div>

      {/* Drawer de guia de mans */}
      {showHandsGuide && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
            onClick={closeHandsGuide}
          />

          {/* Drawer */}
          <div className={cn(
            "fixed bottom-0 left-0 right-0 z-50",
            isClosingHandsGuide ? "animate-slide-down" : "animate-slide-up"
          )}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              {/* Handle */}
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-4 sm:px-6 pb-4">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Combinacions de Poker</h2>
                  <button
                    onClick={closeHandsGuide}
                    className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
                <p className="text-sm text-zinc-400">Ordenades de major a menor valor</p>
                {visibleCommunity.length > 0 && (
                  <div className="mt-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/30">
                    La teva mà actual: <span className="font-bold">{currentHand.rank}</span>
                  </div>
                )}
              </div>

              {/* Llista de mans */}
              <div className="px-4 sm:px-6 pb-6 space-y-3">
                {[
                  { rank: 'Escala de Color', name: 'Escala de Color (Straight Flush)', cards: ['9♠️', '10♠️', 'J♠️', 'Q♠️', 'K♠️'], description: '5 cartes seguides i del mateix pal.' },
                  { rank: 'Poker', name: 'Pòquer (Four of a Kind)', cards: ['8♦️', '8♠️', '8♣️', '8♥️', '//', '2♠️'], description: '4 cartes iguals + 1 carta qualsevol.' },
                  { rank: 'Full', name: 'Full House (Full House)', cards: ['A♣️', 'A♦️', 'A♠️', '//', '10♥️', '10♣️'], description: '3 cartes iguals + 1 parella.' },
                  { rank: 'Color', name: 'Color (Flush)', cards: ['A♠️', 'J♠️', '9♠️', '6♠️', '2♠️'], description: '5 cartes del mateix pal, però NO seguides.' },
                  { rank: 'Escala', name: 'Escala (Straight)', cards: ['5♣️', '6♦️', '7♠️', '8♥️', '9♣️'], description: '5 cartes seguides, però de pals diferents.' },
                  { rank: 'Trio', name: 'Trio (Three of a Kind)', cards: ['Q♠️', 'Q♦️', 'Q♣️', '//', '9♥️', '4♠️'], description: '3 cartes iguals + 2 cartes diferents.' },
                  { rank: 'Doble Parella', name: 'Doble Parella (Two Pair)', cards: ['K♠️', 'K♦️', '//', '4♣️', '4♥️', '//', '9♠️'], description: '2 parelles diferents + 1 carta extra.' },
                  { rank: 'Parella', name: 'Parella (One Pair)', cards: ['J♣️', 'J♦️', '//', '8♠️', '5♥️', '2♦️'], description: '2 cartes iguals + 3 cartes diferents.' },
                  { rank: 'Carta Alta', name: 'Carta Alta (High Card)', cards: ['A♠️', 'Q♦️', '9♣️', '6♥️', '3♦️'], description: 'No hi ha cap combinació, compta la carta més alta.' },
                ].map((hand, index) => {
                  const isCurrentHand = currentHand.rank === hand.rank;
                  return (
                    <div key={index} className={cn(
                      'rounded-xl p-3 sm:p-4 transition-all',
                      isCurrentHand
                        ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20'
                        : 'bg-zinc-800/50 border border-zinc-700/50'
                    )}>
                      <div className="flex items-start gap-3">
                        <div className={cn(
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm',
                          isCurrentHand ? 'bg-amber-500 text-zinc-900' : 'bg-zinc-700 text-zinc-300'
                        )}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={cn('font-bold text-base sm:text-lg mb-1', isCurrentHand ? 'text-amber-400' : 'text-white')}>
                            {hand.name}
                          </h3>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {hand.cards.map((card, cardIndex) => {
                              if (card === '//') {
                                return <div key={cardIndex} className="flex items-center justify-center px-0.5"><span className="text-zinc-600 text-lg font-bold">/</span></div>;
                              }
                              const suitMatch = card.match(/[♠♥♦♣]️?$/);
                              const suit = suitMatch ? suitMatch[0] : '';
                              const rank = suit ? card.slice(0, -suit.length) : card;
                              const isRed = suit.includes('♥') || suit.includes('♦');
                              return (
                                <div key={cardIndex} className="w-9 h-12 sm:w-10 sm:h-14 bg-white rounded-md shadow flex flex-col items-center justify-center font-bold text-sm sm:text-base" style={{ color: isRed ? '#ef4444' : '#18181b' }}>
                                  <span className="leading-none">{rank}</span>
                                  <span className="text-base sm:text-lg leading-none">{suit.replace('️', '')}</span>
                                </div>
                              );
                            })}
                          </div>
                          <p className={cn('text-xs sm:text-sm', isCurrentHand ? 'text-amber-200' : 'text-zinc-400')}>
                            {hand.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Popup explicatiu dels Outs */}
      {showOutsInfo && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" onClick={() => setShowOutsInfo(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md">
            <div className="bg-zinc-900 rounded-2xl shadow-2xl p-5 sm:p-6 border border-zinc-800 animate-fade-in">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-cyan-400 mb-1">Què són els Outs?</h3>
                  <p className="text-sm text-zinc-400">Com calcular les teves probabilitats</p>
                </div>
                <button onClick={() => setShowOutsInfo(false)} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors flex-shrink-0">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-white mb-2">Definició</h4>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Els <span className="text-cyan-400 font-semibold">outs</span> són les cartes que et queden al deck i que poden millorar la teva mà per guanyar la partida.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-xl p-4 border border-amber-500/30">
                  <h4 className="font-bold text-amber-400 mb-2 flex items-center gap-2">📊 Regla 4-2</h4>
                  <div className="space-y-1.5 text-sm">
                    <p className="text-zinc-200"><span className="font-bold text-amber-400">Al Flop:</span> Multiplica els outs × 4 = % de guanyar</p>
                    <p className="text-zinc-200"><span className="font-bold text-amber-400">Al Turn:</span> Multiplica els outs × 2 = % de guanyar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Drawer de Configuració */}
      {showSettings && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" onClick={closeSettings} />
          <div className={cn("fixed bottom-0 left-0 right-0 z-50", isClosingSettings ? "animate-slide-down" : "animate-slide-up")}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>
              <div className="px-4 sm:px-6 pb-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Configuració</h2>
                </div>
                <button onClick={closeSettings} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Configuració del Joc</h3>
                  <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-semibold text-zinc-300">Mostrar probabilitats</label>
                        <p className="text-xs text-zinc-500">Probabilitat de guanyar i outs</p>
                      </div>
                      <button onClick={() => setShowProbabilities(!showProbabilities)} className={cn("relative w-12 h-7 rounded-full transition-colors", showProbabilities ? "bg-cyan-500" : "bg-zinc-700")}>
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform", showProbabilities ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-3">Accions</h3>
                  <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                    <button onClick={handleExitGame} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-400 rounded-lg font-semibold transition-colors">
                      <LogOut className="w-5 h-5" />
                      Sortir de la partida
                    </button>
                    <p className="text-xs text-zinc-500 mt-2 text-center">Les teves fitxes es guardaran automàticament</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Drawer d'Informació */}
      {showInfo && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in" onClick={closeInfo} />
          <div className={cn("fixed bottom-0 left-0 right-0 z-50", isClosingInfo ? "animate-slide-down" : "animate-slide-up")}>
            <div className="bg-zinc-900 rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-zinc-900 pt-3 pb-2 flex justify-center">
                <div className="w-12 h-1.5 bg-zinc-700 rounded-full" />
              </div>
              <div className="px-4 sm:px-6 pb-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Informació del Joc</h2>
                </div>
                <button onClick={closeInfo} className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-6">
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-xl p-4 border border-cyan-500/30 mb-4">
                  <h4 className="font-bold text-cyan-400 mb-2">OBJECTIU DEL JOC</h4>
                  <p className="text-sm text-zinc-200 leading-relaxed">Fer la millor mà de 5 cartes utilitzant les teves 2 cartes més les 5 cartes comunitàries</p>
                </div>
                <div className="space-y-3">
                  <h4 className="font-bold text-white text-sm uppercase tracking-wider mb-4">Rondes del Joc</h4>
                  {[
                    { num: 1, name: 'Pre-flop', desc: 'Reps 2 cartes privades. Hi ha small blind, big blind i dealer.' },
                    { num: 2, name: 'Flop', desc: 'Surten 3 cartes comunitàries al centre de la taula.' },
                    { num: 3, name: 'Turn', desc: 'Surt la 4a carta comunitària. Nova ronda d\'apostes.' },
                    { num: 4, name: 'River', desc: 'Surt la 5a carta comunitària. Última ronda d\'apostes.' },
                    { num: 5, name: 'Showdown', desc: 'Es mostren les cartes dels jugadors. Guanya la millor mà de 5 cartes.' },
                  ].map((round) => (
                    <div key={round.num} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700/50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold text-sm">{round.num}</div>
                        <div className="flex-1">
                          <h5 className="font-bold text-amber-400 text-sm mb-1.5">{round.name}</h5>
                          <p className="text-xs text-zinc-300 leading-relaxed">{round.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Pantalla de resum final */}
      {showFinalSummary && (
        <>
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 animate-fade-in" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-zinc-900 rounded-3xl shadow-2xl max-w-md w-full border border-zinc-800 overflow-hidden">
              <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-6 border-b border-zinc-800">
                <div className="text-center">
                  <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Partida Finalitzada</h2>
                  <p className="text-zinc-400 text-sm">{maxRounds} rondes completades</p>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Resultats */}
                <div className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-sm">Fitxes inicials</span>
                      <span className="text-white font-semibold">{initialChips}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-zinc-400 text-sm">Fitxes finals</span>
                      <span className="text-white font-semibold">{playerChips[user?.username || ''] || 0}</span>
                    </div>
                    <div className="h-px bg-zinc-700 my-3" />
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-white">Resultat</span>
                      <span className={cn(
                        "text-2xl font-bold flex items-center gap-1",
                        (playerChips[user?.username || ''] || 0) > initialChips ? "text-green-400" : 
                        (playerChips[user?.username || ''] || 0) < initialChips ? "text-red-400" : "text-zinc-400"
                      )}>
                        {(playerChips[user?.username || ''] || 0) > initialChips && "+"}
                        {(playerChips[user?.username || ''] || 0) - initialChips}
                        <Coins className="w-5 h-5" />
                      </span>
                    </div>
                  </div>

                  {(playerChips[user?.username || ''] || 0) > initialChips && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                      <p className="text-green-400 font-semibold">🎉 Enhorabona! Has guanyat fitxes</p>
                    </div>
                  )}
                  
                  {(playerChips[user?.username || ''] || 0) < initialChips && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400 font-semibold">Has perdut fitxes. Més sort la propera vegada!</p>
                    </div>
                  )}

                  {(playerChips[user?.username || ''] || 0) === initialChips && (
                    <div className="bg-zinc-500/10 border border-zinc-500/30 rounded-xl p-4 text-center">
                      <p className="text-zinc-400 font-semibold">Has empatat!</p>
                    </div>
                  )}
                </div>

                {/* Botons */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleExitGame}
                    variant="outline"
                    className="flex-1 h-12 gap-2 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Inici
                  </Button>
                  <Button
                    onClick={async () => {
                      await handleExitGame();
                      setTimeout(() => {
                        navigate('/online');
                      }, 100);
                    }}
                    className="flex-1 h-12 gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500"
                  >
                    <RotateCcw className="w-5 h-5" />
                    Nova partida
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

